import { format, parseISO } from 'date-fns';
import { ErrorMessage, Field, Formik } from 'formik';
import { Alert, Button, Col, Container, Form, FormControl, Row, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { useToast } from '../../context/ToastContext';
import { useCreatePublicWaiverMutation } from '../../services/endpoints/people/waiver';
import { useGetCoursePublicQuery } from '../../services/endpoints/schedule/course';

const waiverSchema = Yup.object().shape({
  child_first_name: Yup.string().required('Child first name is required'),
  child_last_name: Yup.string().required('Child last name is required'),
  parent_first_name: Yup.string().required('Parent first name is required'),
  parent_last_name: Yup.string().required('Parent last name is required'),
  parent_email_address: Yup.string().email('Invalid email').required('Parent email is required'),
  parent_mobile_number: Yup.string().required('Parent mobile number is required'),
  agree_to_terms: Yup.boolean().oneOf([true], 'You must accept the terms and conditions')
});

export const PublicWaiverForm = () => {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('course_id');
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [createPublicWaiver, { isLoading: isSubmitting }] = useCreatePublicWaiverMutation();

  const { data: courseData, isLoading: courseLoading } = useGetCoursePublicQuery(courseId ?? '', {
    skip: !courseId
  });

  const course = courseData?.course;

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={8}>
          {course ? (
            <>
              <h2 className="mb-4">{course.title}</h2>

              <div className="mb-4">
                {course.lessons?.length > 0 && (
                  <div className="text-muted">
                    <strong>Class Dates:</strong>{' '}
                    {course.lessons
                      .map((lesson) => {
                        try {
                          return format(parseISO(lesson.date), 'MMMM d, yyyy');
                        } catch {
                          return lesson.date;
                        }
                      })
                      .join(', ')}
                  </div>
                )}
              </div>
            </>
          ) : null}

          {courseLoading && (
            <div className="my-4 text-center">
              <Spinner animation="border" />
            </div>
          )}

          <embed
            src="https://sfc-public.s3.us-west-2.amazonaws.com/StickyFingersCooking%C2%AE.Updated.Waiver.Generic_updated_April_2024.pdf"
            style={{ height: '30vh', width: '100%' }}
            type="application/pdf"
            aria-label="Waiver form PDF"
          />

          <Formik
            initialValues={{
              child_first_name: '',
              child_last_name: '',
              parent_first_name: '',
              parent_last_name: '',
              parent_email_address: '',
              parent_mobile_number: '',
              agree_to_terms: false
            }}
            validationSchema={waiverSchema}
            onSubmit={async (values) => {
              try {
                const { agree_to_terms, ...submitData } = values;
                await createPublicWaiver({
                  ...submitData,
                  course_id: Number(courseId)
                }).unwrap();
                addToast('Waiver submitted successfully!', 'success');
                navigate('/');
              } catch (err: any) {
                addToast(err?.data?.message || 'Failed to submit waiver', 'error');
              }
            }}
          >
            {({ handleSubmit, touched, errors }) => (
              <Form
                noValidate
                onSubmit={handleSubmit}
                className="mt-4"
              >
                <Row>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="child_first_name"
                    >
                      <Form.Label>Child First Name</Form.Label>
                      <Field
                        name="child_first_name"
                        as={Form.Control}
                        isInvalid={touched.child_first_name && !!errors.child_first_name}
                      />
                      <ErrorMessage name="child_first_name">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="child_last_name"
                    >
                      <Form.Label>Child Last Name</Form.Label>
                      <Field
                        name="child_last_name"
                        as={Form.Control}
                        isInvalid={touched.child_last_name && !!errors.child_last_name}
                      />
                      <ErrorMessage name="child_last_name">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="parent_first_name"
                    >
                      <Form.Label>Parent First Name</Form.Label>
                      <Field
                        name="parent_first_name"
                        as={Form.Control}
                        isInvalid={touched.parent_first_name && !!errors.parent_first_name}
                      />
                      <ErrorMessage name="parent_first_name">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="parent_last_name"
                    >
                      <Form.Label>Parent Last Name</Form.Label>
                      <Field
                        name="parent_last_name"
                        as={Form.Control}
                        isInvalid={touched.parent_last_name && !!errors.parent_last_name}
                      />
                      <ErrorMessage name="parent_last_name">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="parent_email_address"
                    >
                      <Form.Label>Parent Email</Form.Label>
                      <Field
                        name="parent_email_address"
                        type="email"
                        as={Form.Control}
                        isInvalid={touched.parent_email_address && !!errors.parent_email_address}
                      />
                      <ErrorMessage name="parent_email_address">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group
                      className="mb-3"
                      controlId="parent_mobile_number"
                    >
                      <Form.Label>Parent Mobile Number</Form.Label>
                      <Field
                        name="parent_mobile_number"
                        as={Form.Control}
                        isInvalid={touched.parent_mobile_number && !!errors.parent_mobile_number}
                      />
                      <ErrorMessage name="parent_mobile_number">
                        {(msg) => <FormControl.Feedback type="invalid">{msg}</FormControl.Feedback>}
                      </ErrorMessage>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group
                  className="mb-3"
                  controlId="agree_to_terms"
                >
                  <Field
                    name="agree_to_terms"
                    type="checkbox"
                    as={Form.Check}
                    label="I accept the terms."
                    isInvalid={touched.agree_to_terms && !!errors.agree_to_terms}
                    feedback={errors.agree_to_terms}
                    feedbackType="invalid"
                  />
                </Form.Group>

                {!courseId && (
                  <Alert variant="warning">
                    No session specified. Please use the link provided.
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || !courseId}
                  className="w-100"
                >
                  {isSubmitting ? (
                    <Spinner
                      animation="border"
                      size="sm"
                    />
                  ) : (
                    'Submit Waiver'
                  )}
                </Button>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};
